import { AxiosRequestConfig, AxiosResponse } from 'axios';
import client from 'api/client';

// ==================== Types ====================

// Subscription data now comes from Stripe API
export interface Subscription {
    id: string; // Stripe subscription ID
    status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing' | 'unpaid';
    product_id?: string; // Stripe product ID
    price_id?: string; // Stripe price ID
    currency: string;
    billing_period: 'month' | 'year';
    amount: number; // Amount in dollars (not cents)
    current_period_start: number; // Unix timestamp
    current_period_end: number; // Unix timestamp
    cancel_at_period_end: boolean;
    canceled_at?: number; // Unix timestamp
    trial_end?: number; // Unix timestamp
    payment_method?: {
        type: string;
        card?: {
            brand: string;
            last4: string;
            exp_month: number;
            exp_year: number;
        };
    };
    billing_settings: {
        auto_billing_enabled: boolean;
        billing_email?: string;
        send_invoice_emails: boolean;
    };
}

// Subscription plans fetched from Stripe
export interface SubscriptionPlan {
    id: string; // Stripe product ID
    name: string;
    description: string;
    monthly_price?: number;
    annual_price?: number;
    monthly_price_id?: string; // Stripe price ID
    annual_price_id?: string; // Stripe price ID
    currency: string;
    features: string[];
    requires_approval: boolean;
}

export interface Invoice {
    id: string; // Stripe invoice ID (e.g., inv_xxxxx)
    invoice_number: string;
    billing_period_start: string | null;
    billing_period_end: string | null;
    total_amount: number;
    currency: string;
    status: string; // 'open', 'paid', 'draft', 'void', etc.
    due_date: string | null;
    days_overdue?: number;
    created: string;
    hosted_invoice_url: string | null;
    invoice_pdf: string | null;
}

// Payment methods are managed directly through Moneris
// Frontend fetches payment methods from Moneris API, not from backend
export interface MonerisPaymentMethod {
    paymentMethodId: string; // Moneris payment method ID
    paymentMethodInformation: {
        cardInformation: {
            cardBrand: string;
            lastFour: string;
            expiryMonth: number;
            expiryYear: number;
        };
    };
}

export interface BillingSettings {
    auto_billing_enabled: boolean;
    billing_email?: string;
    send_invoice_emails: boolean;
    send_payment_reminders: boolean;
    // default_payment_method_id removed - payment methods not stored in DB
}

export interface UsageSummary {
    case_count: number;
    current_tier: string;
    fee_per_case: number;
    billing_month: string;
}

export interface UsageHistory {
    month: string;
    case_count: number;
    tier: string;
    fee_per_case: number;
    total_case_fees: number;
}

export interface Dispute {
    id: number;
    ticket_number: string;
    invoice_id: number;
    subject: string;
    description?: string;
    status: 'OPEN' | 'UNDER_REVIEW' | 'RESOLVED' | 'REJECTED' | 'CLOSED';
    adjustment_amount: number;
    adjustment_reason?: string;
    resolution_notes?: string;
    resolved_at?: string;
    created_at: string;
}

export interface CreateSubscriptionPayload {
    package_type: string;
    billing_cycle_day: number;
    billing_period?: 'MONTHLY' | 'ANNUAL';
    timezone: string;
    currency: string;
}

export interface PayAndCreateSubscriptionPayload {
    package_type: string;
    billing_period: 'MONTHLY' | 'ANNUAL';
    billing_cycle_day: number;
    timezone: string;
    currency: string;
    moneris_payment_method_id: string;
}

export interface ChangePackagePayload {
    new_package_type: string;
    new_billing_period?: 'MONTHLY' | 'ANNUAL';
}

export interface PayInvoicePayload {
    moneris_payment_method_id: string; // Moneris payment method ID from frontend
}

// AddPaymentMethodPayload removed - payment methods managed directly through Moneris

export interface UpdateBillingSettingsPayload {
    auto_billing_enabled?: boolean;
    billing_email?: string;
    send_invoice_emails?: boolean;
    send_payment_reminders?: boolean;
}

export interface CreateDisputePayload {
    invoice_id: number;
    subject: string;
    description: string;
}

// ==================== Subscription APIs ====================

/**
 * Get current subscription from Stripe
 * Queries Stripe API for active subscription data
 */
export function getSubscription(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<Subscription | { subscription: null }>> {
    return client.get('/myaliro_billing/subscription', customConfig);
}

/**
 * Get available subscription plans from Stripe
 * Fetches all active subscription products and prices from Stripe
 */
export function getSubscriptionPlans(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ plans: SubscriptionPlan[] }>> {
    return client.get('/myaliro_billing/subscription/plans', customConfig);
}

export function createSubscription(
    payload: CreateSubscriptionPayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ id: number; package_type: string; status: string; message: string }>> {
    return client.post('/myaliro_billing/subscription', payload, customConfig);
}

export function payAndCreateSubscription(
    payload: PayAndCreateSubscriptionPayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<
    AxiosResponse<{
        id: number;
        package_type: string;
        status: string;
        payment_id: number;
        txn_number: string;
        message: string;
    }>
> {
    return client.post('/myaliro_billing/subscription/payment', payload, customConfig);
}

export function changePackage(
    payload: ChangePackagePayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ id: number; package_type: string; status: string; message: string }>> {
    return client.put('/myaliro_billing/subscription/package', payload, customConfig);
}

export function cancelSubscription(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ message: string }>> {
    return client.post('/myaliro_billing/subscription/cancel', {}, customConfig);
}

/**
 * Create Stripe Customer Portal session
 * Returns URL to Stripe-hosted portal for managing subscriptions and payment methods
 */
export function createCustomerPortalSession(
    returnUrl?: string,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ url: string }>> {
    return client.post('/stripe/customer-portal', { return_url: returnUrl }, customConfig);
}

// ==================== Invoice APIs ====================

export function getInvoices(
    params?: {
        status?: string;
        from_date?: string;
        to_date?: string;
        page?: number;
        per_page?: number;
    },
    customConfig?: AxiosRequestConfig<unknown>
): Promise<
    AxiosResponse<{
        invoices: Invoice[];
        total: number;
        page: number;
        per_page: number;
        pages: number;
    }>
> {
    return client.get('/myaliro_billing/invoices', { ...customConfig, params });
}

export function getOutstandingInvoices(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ invoices: Invoice[]; count: number; total_amount: number }>> {
    return client.get('/myaliro_billing/invoices/outstanding', customConfig);
}

export function getInvoice(
    invoiceId: number,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<Invoice>> {
    return client.get(`/myaliro_billing/invoices/${invoiceId}`, customConfig);
}

export function downloadInvoicePDF(
    invoiceId: number,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<Blob>> {
    return client.get(`/myaliro_billing/invoices/${invoiceId}/pdf`, {
        ...customConfig,
        responseType: 'blob',
    });
}

export function payInvoice(
    invoiceId: number,
    payload: PayInvoicePayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ message: string; txn_number?: string; payment_id?: number }>> {
    return client.post(`/myaliro_billing/invoices/${invoiceId}/pay`, payload, customConfig);
}

// ==================== Payment Method APIs ====================

/**
 * DEPRECATED: Payment methods are now managed through Stripe Customer Portal
 * Use createCustomerPortalSession() to allow users to manage payment methods
 *
 * This endpoint still exists for backward compatibility but should not be used
 * in new code. Stripe Payment Methods are retrieved via Stripe API when needed.
 */
export function getPaymentMethods(customConfig?: AxiosRequestConfig<unknown>): Promise<
    AxiosResponse<{
        payment_methods: Array<{
            payment_method_id: string;
            card_brand: string;
            last_four: string;
            expiry_month: string;
            expiry_year: string;
            created_at: string;
        }>;
    }>
> {
    return client.get('/stripe/payment-methods', customConfig);
}

/**
 * Save payment method reference
 *
 * Two modes:
 * 1. New hosted tokenization: { temporary_token, email }
 * 2. Legacy: { payment_method_id, card_brand, last_four, expiry_month, expiry_year }
 */
export function savePaymentMethod(
    payload:
        | { temporary_token: string; email: string }
        | {
              payment_method_id: string;
              card_brand: string;
              last_four: string;
              expiry_month: string;
              expiry_year: string;
          },
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ message: string }>> {
    return client.post('/myaliro_billing/payment-methods', payload, customConfig);
}

/**
 * Delete payment method from Moneris
 */
export function deletePaymentMethod(
    paymentMethodId: string,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ message: string }>> {
    return client.delete(`/myaliro_billing/payment-methods/${paymentMethodId}`, customConfig);
}

/**
 * Create test payment method (Sandbox only)
 * This directly calls Moneris API to create a test payment method
 */
export function createTestPaymentMethod(
    payload: {
        email: string;
        card_number?: string;
        expiry_month?: string;
        expiry_year?: string;
        cvv?: string;
    },
    customConfig?: AxiosRequestConfig<unknown>
): Promise<
    AxiosResponse<{
        payment_method_id: string;
        card_brand: string;
        last_four: string;
        expiry_month: string;
        expiry_year: string;
        is_test: boolean;
        message?: string;
    }>
> {
    return client.post('/moneris/test/payment-method', payload, customConfig);
}

// ==================== Payment History APIs ====================

export interface PaymentHistoryItem {
    id: string; // Stripe charge ID
    amount: number;
    currency: string;
    status: 'successful' | 'failed' | 'pending';
    status_display: string;
    description: string;
    invoice_number?: string | null;
    payment_method_details?: {
        type: string | null;
        last4: string | null;
        brand: string | null;
    };
    failure_message?: string | null;
    failure_code?: string | null;
    receipt_url?: string | null;
    created: string; // ISO timestamp
    paid: boolean;
    refunded: boolean;
    amount_refunded: number;
}

export function getPaymentHistory(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ payment_history: PaymentHistoryItem[] }>> {
    return client.get('/myaliro_billing/payment-history', customConfig);
}

// ==================== Billing Settings APIs ====================

export function getBillingSettings(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<BillingSettings>> {
    return client.get('/myaliro_billing/settings', customConfig);
}

export function updateBillingSettings(
    payload: UpdateBillingSettingsPayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ message: string }>> {
    return client.put('/myaliro_billing/settings', payload, customConfig);
}

// ==================== Usage Tracking APIs ====================

export function getCurrentUsage(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<UsageSummary>> {
    return client.get('/myaliro_billing/usage/current', customConfig);
}

export function getUsageHistory(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ history: UsageHistory[]; months_count: number }>> {
    return client.get('/myaliro_billing/usage/history', customConfig);
}

// ==================== Dispute APIs ====================

export function createDispute(
    payload: CreateDisputePayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ id: number; ticket_number: string; message: string }>> {
    return client.post('/myaliro_billing/disputes', payload, customConfig);
}

export function getDisputes(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ disputes: Dispute[] }>> {
    return client.get('/myaliro_billing/disputes', customConfig);
}

export function getDispute(
    disputeId: number,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<Dispute>> {
    return client.get(`/myaliro_billing/disputes/${disputeId}`, customConfig);
}

// ==================== Stripe Subscription Packages APIs ====================

export interface SubscriptionPackage {
    type: string;
    name: string;
    monthly_price: number;
    annual_price: number;
    currency: string;
    description: string;
    features: string[];
    requires_approval: boolean;
    case_fee_model: string;
    free_period?: string;
    stripe_monthly_price_id: string;
    stripe_annual_price_id?: string;
    stripe_product_id: string;
}

export function getSubscriptionPackages(
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<{ packages: SubscriptionPackage[] }>> {
    return client.get('/stripe/subscription-packages', customConfig);
}

export interface CreateSubscriptionCheckoutPayload {
    package_type: string;
    billing_period: 'MONTHLY' | 'ANNUAL';
    success_url: string; // Required - frontend provides the redirect URL
    cancel_url: string; // Required - frontend provides the redirect URL
    timezone?: string;
    currency?: string;
}

export interface CreateSubscriptionCheckoutResponse {
    checkout_url: string;
    session_id: string;
}

export function createSubscriptionCheckout(
    payload: CreateSubscriptionCheckoutPayload,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<CreateSubscriptionCheckoutResponse>> {
    return client.post('/stripe/create-subscription-checkout', payload, customConfig);
}

// ==================== Moneris APIs ====================

export interface MonerisPreloadRequest {
    order_id?: string;
    amount?: number;
    email?: string;
    first_name?: string;
    last_name?: string;
    action?: 'tokenize' | 'purchase';
}

export interface MonerisPreloadResponse {
    ticket?: string;
    environment: string;
    // New API fields
    hosted_tokenization_url?: string;
    merchant_id?: string;
    message?: string;
    instructions?: string;
    migration_notice?: string;
}

export function createMonerisPreload(
    data?: MonerisPreloadRequest,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<MonerisPreloadResponse>> {
    return client.post('/moneris/preload', data || {}, customConfig);
}
